/**
 * TidMasterAgGridHook
 *
 * AG Grid hook for the TID Master Browser LiveView.
 *
 * LiveView events received:
 *  "update_tid_master_data" – { rows: [...] } – pushed after filter / tab change.
 *
 * LiveView events pushed:
 *  "tid_master_row_clicked" – { id: <id> } – opens the detail slide-over (row click or Detail button).
 *  "sync_merchant"          – { id: <id> } – triggers on-demand MMS sync for the row's merchant.
 */

const TidMasterAgGridHook = {
  mounted() {
    const container = this.el;
    const rows = JSON.parse(container.dataset.rows || "[]");

    const statusBadge = (value) => {
      if (!value) return "";
      const colours = {
        active:     "#16A34A",
        suspended:  "#D97706",
        terminated: "#DC2626",
      };
      const bg = colours[value] || "#9CA3AF";
      return `<span style="background:${bg};color:#fff;padding:2px 8px;border-radius:9999px;
        font-size:11px;font-weight:600;white-space:nowrap;">${value}</span>`;
    };

    const amountCell = (params) => {
      if (params.value === null || params.value === undefined || params.value === "") return "";
      const n = parseFloat(params.value);
      return isNaN(n) ? params.value : n.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // ── Actions cell renderer ──────────────────────────────────────────────
    const self = this;

    const actionsRenderer = (params) => {
      if (!params.data) return "";
      const { id } = params.data;
      const wrap = document.createElement("div");
      wrap.style.cssText = "display:flex;align-items:center;gap:10px;padding:2px 0;";

      const btn = (label, color, onClick) => {
        const el = document.createElement("button");
        el.textContent = label;
        el.style.cssText = `color:${color};font-size:11px;cursor:pointer;background:none;border:none;padding:0;white-space:nowrap;`;
        el.addEventListener("click", (e) => { e.stopPropagation(); onClick(); });
        return el;
      };

      // Detail — opens the slide-over detail panel
      wrap.appendChild(btn("Detail", "#4f46e5", () =>
        self.pushEvent("tid_master_row_clicked", { id })
      ));

      // Sync — triggers an on-demand MMS sync for this merchant
      wrap.appendChild(btn("Sync", "#0891b2", () =>
        self.pushEvent("sync_merchant", { id: String(id) })
      ));

      return wrap;
    };

    // ── Shared TID dropdown (one per grid, appended to body) ──────────────
    const tidDropdown = document.createElement("div");
    tidDropdown.style.cssText = [
      "position:fixed",
      "background:#fff",
      "border:1px solid #e5e7eb",
      "border-radius:6px",
      "box-shadow:0 4px 16px rgba(0,0,0,0.12)",
      "padding:4px 0",
      "z-index:9999",
      "min-width:130px",
      "display:none",
    ].join(";");
    document.body.appendChild(tidDropdown);

    let activeTidBtn = null;

    const closeTidDropdown = () => {
      tidDropdown.style.display = "none";
      if (activeTidBtn) { activeTidBtn.textContent = "▾"; activeTidBtn = null; }
    };

    document.addEventListener("click", (e) => {
      if (!tidDropdown.contains(e.target) && e.target !== activeTidBtn) {
        closeTidDropdown();
      }
    });

    const tidRenderer = (params) => {
      if (!params.data) return "";
      const allTids = params.data.tid_all || [];
      const first   = allTids[0] || "—";

      const wrap = document.createElement("div");
      wrap.style.cssText = "display:flex;align-items:center;gap:4px;";

      const label = document.createElement("span");
      label.style.cssText = "font-family:monospace;font-size:12px;";
      label.textContent = first;

      const btn = document.createElement("button");
      btn.textContent = "▾";
      btn.style.cssText = "color:#4f46e5;font-size:11px;cursor:pointer;background:none;border:none;padding:0 2px;line-height:1;flex-shrink:0;";

      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (activeTidBtn === btn) { closeTidDropdown(); return; }
        closeTidDropdown();

        // Populate dropdown with this cell's TIDs
        tidDropdown.innerHTML = "";
        allTids.forEach(tid => {
          const item = document.createElement("div");
          item.textContent = tid;
          item.style.cssText = "padding:5px 12px;font-family:monospace;font-size:12px;color:#111827;white-space:nowrap;";
          tidDropdown.appendChild(item);
        });

        // Position below the button
        const rect = btn.getBoundingClientRect();
        tidDropdown.style.top  = (rect.bottom + 4) + "px";
        tidDropdown.style.left = rect.left + "px";
        tidDropdown.style.display = "block";
        btn.textContent = "▴";
        activeTidBtn = btn;
      });

      wrap.appendChild(label);
      wrap.appendChild(btn);
      return wrap;
    };

    // ── Column definitions ─────────────────────────────────────────────────
    const columnDefs = [
      { headerName: "#",               field: "id",                   width: 70,  pinned: "left", sortable: true, filter: false },
      { headerName: "MID",             field: "mid",                  width: 150, pinned: "left" },
      { headerName: "TID",             field: "tid",                  width: 120, cellRenderer: tidRenderer },
      { headerName: "QR ID",           field: "qr_id",                width: 120 },
      { headerName: "Merchant Ref #",  field: "merchant_ref_number",  width: 130 },
      { headerName: "Legal Name",      field: "merchant_name_legal",  width: 200 },
      { headerName: "DBA Name",        field: "merchant_name_dba",    width: 180 },
      { headerName: "Email",           field: "registered_email",     width: 200 },
      { headerName: "Mobile",          field: "registered_mobile",    width: 130 },
      { headerName: "MCC Code",        field: "mcc_code",             width: 90  },
      { headerName: "MCC Description", field: "mcc_description",      width: 160 },
      { headerName: "Bank Code",       field: "bank_code",            width: 100 },
      { headerName: "Bank Name",       field: "bank_name",            width: 150 },
      { headerName: "IBAN",            field: "iban",                 width: 200 },
      { headerName: "Virtual IBAN",    field: "virtual_iban",         width: 200 },
      { headerName: "Schemes",         field: "enabled_schemes",      width: 160 },
      { headerName: "Onboarding Date", field: "onboarding_date",      width: 140 },
      { headerName: "Approved At",     field: "contract_approved_at", width: 160 },
      { headerName: "Onboarding Src",  field: "onboarding_source",    width: 140 },
      { headerName: "Last Txn Date",   field: "last_transaction_date", width: 130 },
      { headerName: "Last Txn Amt",    field: "last_transaction_amount", width: 120, cellRenderer: amountCell, type: "numericColumn" },
      { headerName: "MMS Synced",      field: "mms_synced_at",        width: 160 },
      { headerName: "Status",          field: "status",               width: 110, cellRenderer: p => statusBadge(p.value) },
      { headerName: "Inserted At",     field: "inserted_at",          width: 160 },
      {
        headerName: "Actions",
        colId: "actions",
        pinned: "right",
        width: 110,
        sortable: false,
        filter: false,
        floatingFilter: false,
        resizable: false,
        cellRenderer: actionsRenderer,
      },
    ];

    const gridOptions = {
      columnDefs,
      rowData: rows,
      pagination: true,
      paginationPageSize: 50,
      domLayout: "autoHeight",
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: true,
        minWidth: 70,
      },
      animateRows: true,
      rowSelection: "single",
      sideBar: {
        toolPanels: [
          {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressColumnFilter: false,
              suppressColumnSelectAll: false,
              suppressColumnExpandAll: false,
            },
          },
        ],
      },
      onRowClicked: (event) => {
        if (event.data && event.data.id) {
          this.pushEvent("tid_master_row_clicked", { id: event.data.id });
        }
      },
      overlayNoRowsTemplate:
        `<span style="padding:20px;color:#6B7280;">No TID Master records found. Adjust filters or trigger an MMS sync.</span>`,
    };

    if (window.agGrid && window.agGrid.createGrid) {
      this.gridApi = window.agGrid.createGrid(container, gridOptions);
    } else {
      console.error("TidMasterAgGridHook: agGrid not found on window");
    }

    this.handleEvent("update_tid_master_data", ({ rows: newRows }) => {
      if (this.gridApi) {
        this.gridApi.setGridOption("rowData", newRows || []);
      }
    });
  },

  updated() {
    if (this.gridApi && this.el.dataset.rows) {
      try {
        const rows = JSON.parse(this.el.dataset.rows);
        this.gridApi.setGridOption("rowData", rows);
      } catch (e) {
        console.warn("TidMasterAgGridHook updated(): failed to parse rows", e);
      }
    }
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
    const dropdown = this.el.querySelector && document.getElementById("tid-dropdown");
    if (dropdown) dropdown.remove();
  },
};

export default TidMasterAgGridHook;
